import ElakayImage from '../../../components/images/ElakayImage';
import React from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { getPublicIdentifier, buildShopSellerPath } from "../../../utils/publicIdentifier";
import PageEmptySpace from "../../pageInside/pageEmptySpace";
import CateryName from "../../pageInside/cateryName";
import { toImageUrl } from "../../../config";
import elakayEmptyBox from "../../Layout/icon/elakayEmptyBox.png";
import imgProductLakay from '../../Layout/imgs/md3.jpg';
import { GrView } from "react-icons/gr";
import { MdAdsClick } from "react-icons/md";
import "./SwiperCategoriesMatrix.css";
import { getDefaultTitleStyle } from "../../../utils/titleStyle";
import { getProductPrimaryImage } from "../../auth/elakay-utils";

import "swiper/css";
import "swiper/css/scrollbar";
import "swiper/css/navigation";
import "swiper/css/pagination";

import useElakaySwiper from "../swiperSettings/useElakaySwiper";
import { legacyTableFrom } from "../swiperSettings/swiperSettingsModel";

const DESKTOP_SLIDES_PER_GROUP = 3;

const limitArrayLength = (array = [], l = 4) => {
  if (!Array.isArray(array)) return [];
  if (typeof l !== "number" || l < 0) return [];
  return array.slice(0, l);
}

// Shared resolver (elakay-utils getProductImageUrls). A product with no
// resolvable image gets the same placeholder as onError below -- an <img>
// without src never fires onError and stayed a blank box.
const getImageUrl = (item) => getProductPrimaryImage(item, imgProductLakay);

const COLORS2 = [
  "GhostWhite", "Ivory", "MintCream", "Silver", "White",
  "LightGray", "Gainsboro", "LightSlateGray", "LightSteelBlue",
  "AliceBlue", "AntiqueWhite", "Azure", "Beige", "Bisque",
  "BlanchedAlmond", "Cornsilk", "Cyan", "Chartreuse", "BurlyWood",
  "Coral", "LightBlue", "LightPink", "LightYellow", "MistyRose",
  "PeachPuff", "PowderBlue", "LemonChiffon", "Lavender",
  "HoneyDew", "FloralWhite"
];

// One color per cell, the same on every render (it used to be drawn at random
// each time: the empty cells changed color whenever the page re-rendered).
const getCellColor = (seed, index) => {
  let hash = 0;
  for (const char of String(seed || "")) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return COLORS2[(hash + index) % COLORS2.length];
};

// While the page's categories are loading: as many square slides as a phone
// to a desktop can show, so the block has its final height from the start.
const LOADING_SLIDES = [0, 1, 2].map(() => ({ autantique: "loading" }));

const PubProducts = (props) => {
  const {
    infoSource,
    nbrArr = 4,
    products = []
  } = props;

  let newProducts = limitArrayLength(products.products, nbrArr);

  if (!infoSource) return null;

  const getDefaultDivImage = (index = 0) => {
    return (
      <div
        className="fallback-image-wrapper"
        style={{
          backgroundColor: getCellColor(products?.categoryID || products?.item_id, index),
        }}
      >
        <img
          className="fallback-image"
          src={elakayEmptyBox}
          alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).swiperUserSwiperCategoriesMatrix.t001}
          onError={(event) => {
            event.target.style.display = "none";
          }}
        />
      </div>
    );
  };

  const productALL = () => {
    const totalCells = 4;
    const cells = [];

    newProducts.forEach((product, i) => {
      cells.push(
        <div key={i} className="grid-cell">
          <ElakayImage
            preset="product-card"
            className="card-img-top"
            src={toImageUrl(getImageUrl(product))}
            alt={product.name}
            onError={event => {
              event.target.src = imgProductLakay;
              event.onerror = null;
            }}
          />
        </div>
      );
    });

    // A category with 1 to 3 products shows them over the whole card
    // (.products-grid--N): colored cells without an image looked like
    // pictures that had failed to load. Only a card with no product at all
    // keeps the four placeholders.
    const missingCount = newProducts.length ? 0 : totalCells;
    for (let j = 0; j < missingCount; j++) {
      cells.push(
        <div key={`missing-${j}`} className="grid-cell">
          <div className="empty">
            {getDefaultDivImage(j)}
          </div>
        </div>
      );
    }

    // No outer wrapper — .products-grid IS the root. The old unstyled
    // wrapper div here had no className/sizing of its own (its height was
    // "auto", i.e. content-driven), which silently broke every
    // `.products-grid { height: 100% }` rule for the --standard/
    // --prod-category card variants: a percentage height only resolves
    // against a containing block with a DEFINITE height, and this div's
    // auto height meant .products-grid never actually saw "100% of the
    // card body" — only .matrix-card-body--square worked, and only
    // because it had its own extra "> div { height: 100% }" rule bridging
    // that exact gap. Removing the wrapper fixes all three variants at once.
    return (
      <div className={`products-grid${newProducts.length && newProducts.length < totalCells ? ` products-grid--${newProducts.length}` : ""}`}>
        {cells.map((cell, index) => (
          <React.Fragment key={index}>{cell}</React.Fragment>
        ))}
      </div>
    );
  };

  return productALL();
}

function processData(input) {
  const data = Array.isArray(input) ? input : [input];

  const cleaned = data.map(item => ({
    ...item,
    name: Array.isArray(item?.name)
      ? item.name
        .map(n => (typeof n === "string" ? n.trim() : n))
        .filter(n => typeof n === "string" ? n.length > 0 : Boolean(n))
      : []
  }));

  const allNames = cleaned.flatMap(item => item.name);
  const nameCounts = allNames.reduce((acc, name) => {
    acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const GENDERS = new Set(["Men", "Women", "Boys", "Girls", "Babies"]);

  return cleaned.map(item => {
    const filteredNames = item.name.filter(name => nameCounts[name] < cleaned.length);
    const genderGroup = filteredNames.find(name => GENDERS.has(name));
    const main = item.name?.[0] ?? "";
    const cateryName = [genderGroup, main].filter(Boolean).join(" ").trim();

    return {
      ...item,
      cateryName: cateryName || main || null,
      categoryName: cateryName || main || null
    };
  });
}


const dataEmptyPage = () => {
  const min = 1;
  const max = 15;
  const a = Math.floor(Math.random() * (max - min + 1)) + min;
  if (a === 1) {
    return { slogan: "Elakay works for", text: ["Art Lovers", "Trend Seekers", "Explorers", "YOU!"] };
  } else if (a === 2) {
    return { slogan: "Elakay empowers", text: ["Shoppers", "Supporters", "Smart Shoppers", "YOU!"] };
  } else if (a === 3) {
    return { slogan: "Elakay is for", text: ["Dreamers", "Makers", "Believers", "YOU!"] };
  } else if (a === 4) {
    return { slogan: "Elakay connects", text: ["Creators", "Explorers", "Innovators", "YOU!"] };
  } else if (a === 5) {
    return { slogan: "Elakay supports", text: ["Hustlers", "Visionaries", "Builders", "YOU!"] };
  } else if (a === 6) {
    return { slogan: "Elakay celebrates", text: ["Artists", "Designers", "Crafters", "YOU!"] };
  } else if (a === 7) {
    return { slogan: "Elakay unites", text: ["Communities", "Cultures", "Crafts", "YOU!"] };
  } else if (a === 8) {
    return { slogan: "Elakay inspires", text: ["Trendsetters", "Bargain Hunters", "Style Seekers", "YOU!"] };
  } else if (a === 9) {
    return { slogan: "Elakay fuels", text: ["Fashionistas", "Foodies", "Tech Enthusiasts", "YOU!"] };
  } else if (a === 10) {
    return { slogan: "Elakay is for", text: ["Collectors", "Curators", "Creators", "YOU!"] };
  } else if (a === 11) {
    return { slogan: "Elakay empowers", text: ["Adventurers", "Storytellers", "Visionaries", "YOU!"] };
  } else if (a === 12) {
    return { slogan: "Elakay connects", text: ["Stylists", "Homebodies", "Wanderers", "YOU!"] };
  } else if (a === 13) {
    return { slogan: "Elakay celebrates", text: ["Givers", "Receivers", "Believers", "YOU!"] };
  } else if (a === 14) {
    return { slogan: "Elakay is for", text: ["Leaders", "Thinkers", "Doers", "YOU!"] };
  } else if (a === 15) {
    return { slogan: "Elakay supports", text: ["Innovators", "Entrepreneurs", "Visionaries", "YOU!"] };
  } else {
    return { slogan: "Elakay works for", text: ["Art Lovers", "Trend Seekers", "Smart Shoppers", "YOU!"] };
  }
}

function addObjectWithAutantique(array = []) {
  if (!Array.isArray(array)) return [];
  const PLACEHOLDER = {
    item_name: null,
    item_id : null,
    publicId: null,
    categoryID: null,
    name: null,
    categoryIDList: null,
    products: null,
    autantique: "vide",
  };

  const normalizeOne = (item) => {
    if (!item) return { ...PLACEHOLDER };

    if (
      item.autantique === false ||
      (item.categoryID == null && item.name == null && item.products == null)
    ) {
      return { ...PLACEHOLDER };
    }

    const name = Array.isArray(item.name)
      ? item.name
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter((v) => (typeof v === "string" ? v.length > 0 : Boolean(v)))
      : null;
    
    const item_name = typeof item.item_name === "string" ? item.item_name.trim() : null;
    const item_id = typeof item.item_id === "string" ? item.item_id.trim() : null;
    const publicId = typeof item.publicId === "string" ? item.publicId.trim() : null;
    const categoryID =
      typeof item.categoryID === "string" ? item.categoryID.trim() : null;

    const categoryIDList = Array.isArray(item.categoryIDList)
      ? item.categoryIDList
        .map((v) => (typeof v === "string" ? v.trim() : v))
        .filter(Boolean)
      : categoryID
        ? [categoryID]
        : null;
    const products = Array.isArray(item.products) ? item.products : null;

    return {
      categoryID,
      item_name,
       item_id,
      publicId,
      name,
      categoryIDList,
      products,
      autantique: "full",
    };
  };

  return array.map(normalizeOne);
}


const SwiperCategoriesMatrix = (props) => {
  const {
    nbrArr = 4,
    title = "Shop Categories",
    infoSource,
    myStyles,
    wm0 = 1.3,
    wm640 = 2,
    wm768 = 3,
    wm1024 = 3,
    wm1200 = 3,
    inView = false,
    setSearch,
    setCi,
    categoriesMatrix = [],
    // The page is still fetching its categories: square placeholder slides
    // hold the block's height (nothing below moves when they arrive).
    loading = false,
    loadProductsByArrival,
    prodCategory = false,
    textFoorter = false,
    renderFullCardSquare = true,
    // ============================================
    // 🎬 PROPS ANIMATION CONTAINER (0-32)
    // ============================================
    type_Animation = null, // "0_type_Animation" à "32_type_Animation"
    animation = false, // Rétrocompatibilité - ancien système
    backgroundColor = "#F4F6F8",
    containerFusion = false, // Fond, bordure et ombre transparents — fusionne le conteneur avec la page
    containerBorder = null, // ex: "1px solid #e5e7eb"
    containerBorderRadius = null, // ex: "12px"
    containerBoxShadow = null, // ex: "0 4px 20px rgba(0,0,0,0.08)"

    // ============================================
    // 🎨 PROPS TITRE - POLICE & STYLE
    // ============================================
    // Défauts non fixés en dur : voir getDefaultTitleStyle() (utils/titleStyle.js)
    // — AnimationSettings → General → "Default Title Styles" fournit la valeur
    // par défaut si aucune de ces props n'est explicitement passée.
    titleFontFamily, // Police du titre: "Arial", "Roboto", "Poppins", etc.
    titleFontSize, // "1.5rem", "2rem", "24px", etc.
    titleFontWeight, // Graisse: 400, 500, 600, 700, 800, 900
    titleFontStyle, // Style: "normal", "italic", "oblique"
    titleLetterSpacing, // Espacement lettres: "-1px", "0", "2px"
    titleLineHeight, // Hauteur de ligne: 1, 1.2, 1.5, "normal"
    titleTextTransform, // Transform: "none", "uppercase", "lowercase", "capitalize"
    titleTextAlign, // Alignement: "left", "center", "right"

    // ============================================
    // 🎨 PROPS TITRE - COULEURS
    // ============================================
    titleColor, // Couleur texte du titre
    titleTextShadow, // Ombre du texte

    // ============================================
    // 🎬 PROPS TITRE - ANIMATION (0-32)
    // ============================================
    titleAnimation = null, // "animated_0" à "animated_32" ou null pour désactiver
    titleAnimationColors = null, // { primary: "#...", accent: "#..." } ou null pour défaut

    // ============================================
    // 🎬 PROPS HEADER - ANIMATION (0-32)
    // ============================================
    headerAnimation = null, // "animated_0" à "animated_32" ou null pour désactiver
    headerAnimationColors = null, // { primary: "#...", accent: "#..." } ou null pour défaut

    // ============================================
    // 🎨 PROPS HEADER - STYLE
    // ============================================
    headerBackgroundColor = "transparent", // Couleur de fond du header
    headerPadding = null, // Padding personnalisé: "1rem", "10px 20px"
    headerMarginBottom = null, // Marge en bas: "1rem", "20px"

    // ============================================
    // 🎨 COULEURS D'ANIMATION GLOBALES
    // ============================================
    animationColors = {
      // Type 0 - Version originale (arc-en-ciel)
      "0_type_Animation": {
        primary: "#007399",
        accent: "#3b82f6",
        secondary: "#8b5cf6",
      },
      // Type 1 - Rose et Violet
      "1_type_Animation": {
        primary: "#e91e63",
        accent: "#9c27b0",
        light1: "#fce4ec",
        light2: "#f3e5f5",
      },
      // Type 2 - Vert et Jaune
      "2_type_Animation": {
        primary: "#4caf50",
        accent: "#ffeb3b",
        light1: "#e8f5e9",
        light2: "#fffde7",
      },
      // Type 3 - Orange et Rouge
      "3_type_Animation": {
        primary: "#ff9800",
        accent: "#f44336",
        light1: "#fff3e0",
        light2: "#ffebee",
      },
      // Type 4 - Bleu et Indigo
      "4_type_Animation": {
        primary: "#2196f3",
        accent: "#3f51b5",
        light1: "#e3f2fd",
        light2: "#e8eaf6",
      },
      // Type 5 - Cyan et Teal
      "5_type_Animation": {
        primary: "#00bcd4",
        accent: "#009688",
        light1: "#e0f7fa",
        light2: "#e0f2f1",
      },
      // Type 6 - Or et Bronze (Premium)
      "6_type_Animation": {
        primary: "#ffd700",
        accent: "#cd7f32",
        light1: "#fffde7",
        light2: "#fff8e1",
      },
      // Type 7 - Argent et Platine
      "7_type_Animation": {
        primary: "#c0c0c0",
        accent: "#e5e4e2",
        light1: "#fafafa",
        light2: "#f5f5f5",
      },
      // Type 8 - Neon Magenta et Cyan
      "8_type_Animation": {
        primary: "#ff00ff",
        accent: "#00ffff",
        light1: "#fff0ff",
        light2: "#e0ffff",
      },
      // Type 9 - Rainbow
      "9_type_Animation": {
        primary: "#ff0000",
        accent: "#00ff00",
        secondary: "#0000ff",
        tertiary: "#ffff00",
      },
      // Type 10 - Deep Purple et Lavender
      "10_type_Animation": {
        primary: "#673ab7",
        accent: "#b39ddb",
        light1: "#ede7f6",
        light2: "#d1c4e9",
      },
      // Type 11 - Lime et Chartreuse
      "11_type_Animation": {
        primary: "#cddc39",
        accent: "#8bc34a",
        light1: "#f9fbe7",
        light2: "#dcedc8",
      },
      // Type 12 - Pink et Fuchsia
      "12_type_Animation": {
        primary: "#e91e63",
        accent: "#9c27b0",
        light1: "#fce4ec",
        light2: "#f3e5f5",
      },
      // Type 13 - Amber et Honey
      "13_type_Animation": {
        primary: "#ffc107",
        accent: "#ffa000",
        light1: "#fff8e1",
        light2: "#ffecb3",
      },
      // Type 14 - Brown et Tan
      "14_type_Animation": {
        primary: "#795548",
        accent: "#bcaaa4",
        light1: "#efebe9",
        light2: "#d7ccc8",
      },
      // Type 15 - Blue Grey et Slate
      "15_type_Animation": {
        primary: "#607d8b",
        accent: "#90a4ae",
        light1: "#eceff1",
        light2: "#cfd8dc",
      },
      // Type 16 - Coral et Peach
      "16_type_Animation": {
        primary: "#ff7f50",
        accent: "#ffdab9",
        light1: "#fff5ee",
        light2: "#ffefd5",
      },
      // Type 17 - Blanc et Turquoise Premium
      "17_type_Animation": {
        primary: "#007399",
        accent: "#4facfe",
        light1: "#e8f4f8",
        light2: "#d4ecf7",
      },
      // Type 18 - Matrix Digital Rain
      "18_type_Animation": {
        primary: "#00ff41",
        accent: "#00c832",
        light1: "#0d1a0d",
        light2: "#001a00",
      },
      // Type 19 - Neon Glow
      "19_type_Animation": {
        primary: "#ff0080",
        accent: "#00ffff",
        light1: "#1a0a1a",
        light2: "#0a1a1a",
      },
      // Type 20 - Liquid Blob
      "20_type_Animation": {
        primary: "#667eea",
        accent: "#764ba2",
        light1: "#eef2ff",
        light2: "#faf5ff",
      },
      // Type 21 - Holographic / Prism
      "21_type_Animation": {
        primary: "#ff0080",
        accent: "#40e0d0",
        secondary: "#ff8c00",
        tertiary: "#8000ff",
      },
      // Type 22 - Electric Storm
      "22_type_Animation": {
        primary: "#ffff00",
        accent: "#ffc800",
        light1: "#1a1a2e",
        light2: "#16213e",
      },
      // Type 23 - Orbital / Sunset
      "23_type_Animation": {
        primary: "#ffc864",
        accent: "#ff9632",
        light1: "#fff8f0",
        light2: "#fff0e0",
      },
      // Type 24 - Water Ripples / Ocean
      "24_type_Animation": {
        primary: "#0096c8",
        accent: "#00c8fa",
        light1: "#e6f7ff",
        light2: "#ccefff",
      },
      // Type 25 - Smoke Rising
      "25_type_Animation": {
        primary: "#c8c8c8",
        accent: "#969696",
        light1: "#f5f5f5",
        light2: "#e0e0e0",
      },
      // Type 26 - Confetti Celebration
      "26_type_Animation": {
        primary: "#ff6b6b",
        accent: "#4ecdc4",
        secondary: "#ffe66d",
        tertiary: "#95e1d3",
      },
      // Type 27 - Sparkle Stars
      "27_type_Animation": {
        primary: "#ffffc8",
        accent: "#ffffff",
        light1: "#0c1445",
        light2: "#1a237e",
      },
      // Type 28 - Hexagon Grid / Tech Blue
      "28_type_Animation": {
        primary: "#3498db",
        accent: "#2ecc71",
        light1: "#ecf0f1",
        light2: "#bdc3c7",
      },
      // Type 29 - Triangle Pattern / Soft Pink
      "29_type_Animation": {
        primary: "#ff9a9e",
        accent: "#fad0c4",
        light1: "#fff5f5",
        light2: "#fff0ed",
      },
      // Type 30 - Morphing Square / Lavender
      "30_type_Animation": {
        primary: "#a18cd1",
        accent: "#fbc2eb",
        light1: "#f5f0ff",
        light2: "#fff0f8",
      },
      // Type 31 - Energy Burst
      "31_type_Animation": {
        primary: "#ff6464",
        accent: "#6464ff",
        light1: "#fff0f0",
        light2: "#f0f0ff",
      },
      // Type 32 - Northern Lights / Aurora
      "32_type_Animation": {
        primary: "#00ff80",
        accent: "#00c8ff",
        secondary: "#8000ff",
        light1: "#0c1445",
        light2: "#1a237e",
      },
    }
  } = props;

  const classSlider = props.classSlider ? props.classSlider : "styles.sliderProduct";
  const loadedCategoriesMatrix = prodCategory
    ? processData(addObjectWithAutantique(categoriesMatrix))
    : processData(addObjectWithAutantique(categoriesMatrix?.categories));
  const waiting = loading && !loadedCategoriesMatrix.length;
  const newcategoriesMatrix = waiting ? LOADING_SLIDES : loadedCategoriesMatrix;
  const getCategoryPayload = (items = {}) => {
    const rawId =
      items?.categoryID ??
      items?.categoryId ??
      items?.catID ??
      items?._id ??
      items?.id ??
      (Array.isArray(items?.categoryIDList) ? items.categoryIDList[0] : null);
    const catId = rawId && typeof rawId === "object"
      ? rawId._id ?? rawId.id ?? rawId.categoryID ?? rawId.catID ?? null
      : rawId;
    const rawName = items?.cateryName ?? items?.categoryName ?? items?.item_name ?? items?.name;
    const catName = Array.isArray(rawName)
      ? rawName.find((value) => String(value || "").trim()) || "All Products"
      : String(rawName || "").trim() || "All Products";
    return { catId, catName };
  };

  const handleCategoryClick = (e, items = {}) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const { catId, catName } = getCategoryPayload(items);
    loadProductsByArrival?.(catId, catName);
  };

  const getProductCategoryLink = (items = {}) => {
    const productId =
      items?.publicId ??
      items?.item_id ??
      items?.productId ??
      items?._id ??
      items?.id ??
      items?.categoryID;

    return productId ? `/product/${productId}` : "#";
  };

  const getProductCategoryName = (items = {}) => {
    const rawName = items?.item_name ?? items?.cateryName ?? items?.categoryName ?? items?.name;
    if (Array.isArray(rawName)) {
      return rawName.find((value) => String(value || "").trim()) || "";
    }
    return String(rawName || "").trim();
  };

  const getShopCategoryLink = (items = {}) => {
    if (!categoriesMatrix?.owner || !items?.categoryID) return "#";
    const categoryId = getPublicIdentifier(items, items.categoryID);
    return buildShopSellerPath(
      { publicId: categoriesMatrix?.ownerPublicId },
      categoriesMatrix.owner,
      `?ci=${categoryId}${props.shopId ? `&shop=${encodeURIComponent(props.shopId)}` : ""}`
    );
  };

  const getViewPageTitle = () => (
    infoSource?.laguangeElakay?.("title", infoSource.languageAPI)?.viewPage
    || infoSource?.language?.(infoSource, "Link", "seeMore")
    || "View page"
  );

  // // Déterminer le type d'animation et les classes CSS
  // const getAnimationClass = () => {
  //   if (type_Animation) {
  //     const validTypes = [
  //       "1_type_Animation", "2_type_Animation", "3_type_Animation", "4_type_Animation",
  //       "5_type_Animation", "6_type_Animation", "7_type_Animation", "8_type_Animation",
  //       "9_type_Animation", "10_type_Animation", "11_type_Animation", "12_type_Animation",
  //       "13_type_Animation", "14_type_Animation", "15_type_Animation", "16_type_Animation"
  //     ];
  //     if (validTypes.includes(type_Animation)) {
  //       return type_Animation;
  //     }
  //   }
  //   return "";
  // };

  // const animationClass = getAnimationClass();
  // const isAnimated = animationClass !== "";

  // // Classes CSS conditionnelles
  // const containerClass = isAnimated
  //   ? `matrix-swiper-container ${animationClass}`
  //   : "matrix-swiper-container";

  // const headerClass = isAnimated
  //   ? `matrix-header ${animationClass}`
  //   : "matrix-header";

  // const titleClass = isAnimated
  //   ? `matrix-title ${animationClass}`
  //   : "matrix-title";

  // const swiperClass = isAnimated
  //   ? `matrix-swiper ${animationClass}`
  //   : "matrix-swiper";

  // // Obtenir les couleurs d'animation selon le type
  // const getCurrentColors = () => {
  //   if (!isAnimated) return null;
  //   return animationColors[animationClass] || animationColors["2_type_Animation"];
  // };

  // const colors = getCurrentColors();

  // // Style inline pour le container non-animé
  // const containerStyle = !isAnimated
  //   ? { backgroundColor, ...myStyles }
  //   : myStyles;

  // // Style CSS variables pour les couleurs d'animation
  // const animationStyle = isAnimated && colors
  //   ? {
  //     '--animation-primary': colors.primary,
  //     '--animation-accent': colors.accent,
  //     '--animation-secondary': colors.secondary || colors.primary,
  //     '--animation-tertiary': colors.tertiary || colors.accent,
  //     '--animation-light1': colors.light1 || '#f0f0f0',
  //     '--animation-light2': colors.light2 || '#e0e0e0',
  //     ...myStyles
  //   }
  //   : myStyles;

    // ============================================
  // 🎬 LOGIQUE ANIMATION CONTAINER
  // ============================================
  const getAnimationClass = () => {
    if (type_Animation) {
      const validTypes = Array.from({ length: 33 }, (_, i) => `${i}_type_Animation`);
      if (validTypes.includes(type_Animation)) {
        return type_Animation;
      }
    }
    if (animation === true || animation === "true") {
      return "0_type_Animation";
    }
    return "";
  };

  const animationClass = getAnimationClass();
 
  const isAnimated = animationClass !== "";


  // ============================================
  // 🎬 LOGIQUE ANIMATION TITRE
  // ============================================
  const getTitleAnimationClass = () => {
    if (titleAnimation === false || titleAnimation === "none") {
      return "";
    }
    if (titleAnimation) {
      const validAnimations = Array.from({ length: 33 }, (_, i) => `animated_${i}`);
      if (validAnimations.includes(titleAnimation)) {
        return titleAnimation;
      }
    }
    // Si pas de titleAnimation spécifié mais container animé, utiliser l'animation correspondante
    // if (isAnimated && titleAnimation === null) {
    //   const typeNumber = animationClass.replace("_type_Animation", "");
    //   return `animated_${typeNumber}`;
    // }
    return "";
  };

  const titleAnimClass = getTitleAnimationClass();

  // ============================================
  // 🎬 LOGIQUE ANIMATION HEADER
  // ============================================
  const getHeaderAnimationClass = () => {
    if (headerAnimation === false || headerAnimation === "none") {
      return "";
    }
    if (headerAnimation) {
      const validAnimations = Array.from({ length: 33 }, (_, i) => `animated_${i}`);
      if (validAnimations.includes(headerAnimation)) {
        return headerAnimation;
      }
    }
    // Si pas de headerAnimation spécifié mais container animé, utiliser l'animation correspondante
    // if (isAnimated && headerAnimation === null) {
    //   const typeNumber = animationClass.replace("_type_Animation", "");
    //   return `animated_${typeNumber}`;
    // }
    return "";
  };

  const headerAnimClass = getHeaderAnimationClass();

  // ============================================
  // 🎨 CLASSES CSS CONDITIONNELLES
  // ============================================
  const containerClass = [
    "matrix-swiper-container",
    !prodCategory ? "matrix-swiper-container--standard" : "",
    isAnimated ? animationClass : "",
  ].filter(Boolean).join(" ");

  const headerClass = headerAnimClass
    ? `matrix-header ${headerAnimClass}`
    : "matrix-header";

  const titleClass = titleAnimClass
    ? `matrix-title ${titleAnimClass}`
    : "matrix-title";

  // Options: page instance > Animation Settings -> Swiper Settings > origin.
  // wm* passed by the page are that instance's own slides-per-view.
  const swp = useElakaySwiper("categoriesMatrix", {
    swiperOptions: props.swiperOptions,
    legacyTable: legacyTableFrom({ wm0, wm640, wm768, wm1024, wm1200 }, { 0: 15, 1024: 20 }),
    legacyExplicit: ["wm0", "wm640", "wm768", "wm1024", "wm1200"].some((key) => props[key] !== undefined),
    autoNavigation: Boolean(infoSource?.navigation),
    slideCount: newcategoriesMatrix?.length || 0,
  });
  const swiperClass = isAnimated
    ? `matrix-swiper ${animationClass}`
    : "matrix-swiper";

  // ============================================
  // 🎨 OBTENIR LES COULEURS D'ANIMATION
  // ============================================
  const getCurrentColors = () => {
    if (!isAnimated) return null;
    return animationColors[animationClass] || animationColors["0_type_Animation"];
  };

  const colors = getCurrentColors();

  // ============================================
  // 🎨 STYLES INLINE
  // ============================================

  // Style de bordure / rayon / ombre du container
  const borderStyle = {
    ...(containerBorder && { border: containerBorder }),
    ...(containerBorderRadius && { borderRadius: containerBorderRadius }),
    ...(containerBoxShadow && { boxShadow: containerBoxShadow }),
  };

  // Style de fusion : fond, bordure et ombre transparents (le conteneur se fond avec la page)
  const fusionStyle = containerFusion
    ? { backgroundColor: "transparent", border: "none", boxShadow: "none" }
    : null;

  // Style du container
  const containerStyle = !isAnimated
    ? { backgroundColor, ...myStyles, ...borderStyle, ...fusionStyle }
    : { ...myStyles, ...borderStyle, ...fusionStyle };

  // CSS Variables pour les animations du container
  const animationStyle =
    isAnimated && colors
      ? {
          "--anim-primary": colors.primary,
          "--anim-accent": colors.accent,
          "--anim-secondary": colors.secondary || colors.primary,
          "--anim-tertiary": colors.tertiary || colors.accent,
          "--anim-light1": colors.light1 || "#f0f0f0",
          "--anim-light2": colors.light2 || "#e0e0e0",
          ...myStyles,
          ...borderStyle,
          ...fusionStyle,
        }
      : { ...myStyles, ...borderStyle, ...fusionStyle };

  // Style du Header
  const headerStyle = {
    backgroundColor: headerBackgroundColor,
    ...(headerPadding && { padding: headerPadding }),
    ...(headerMarginBottom && { marginBottom: headerMarginBottom }),
    // CSS Variables pour les couleurs d'animation du header
    ...(headerAnimClass &&
      headerAnimationColors && {
        "--header-anim-primary": headerAnimationColors.primary,
        "--header-anim-accent": headerAnimationColors.accent,
      }),
  };

  // Style du Titre — priorité : props explicites > AnimationSettings
  // "Default Title Styles" > défauts codés en dur (voir titleStyle.js).
  const titlePresentation = getDefaultTitleStyle(infoSource?.allSettings?.defaultTitleStyle, {
    fontFamily: titleFontFamily,
    fontSize: titleFontSize,
    fontWeight: titleFontWeight,
    fontStyle: titleFontStyle,
    letterSpacing: titleLetterSpacing,
    lineHeight: titleLineHeight,
    textTransform: titleTextTransform,
    textAlign: titleTextAlign,
    color: titleColor,
    textShadow: titleTextShadow,
  });
  const titleStyle = {
    ...titlePresentation.style,
    // CSS Variables pour les couleurs d'animation du titre
    ...(titleAnimClass &&
      titleAnimationColors && {
        "--anim-primary": titleAnimationColors.primary,
        "--anim-accent": titleAnimationColors.accent,
      }),
  };

  const cardEmpty = (emptyIndex = 0) => {
    const useSquareCard = renderFullCardSquare && !prodCategory;
    const animationVariant = emptyIndex % 7;
    return (
    <div className={`matrix-empty-card swiper-categories-matrix-empty${prodCategory ? "" : " matrix-empty-card--standard"}${useSquareCard ? " matrix-empty-card--square" : ""}`}>
      <div className="empty-card-aspect"
          >
        {inView && !prodCategory && !useSquareCard && (
          <div className="matrix-card-header matrix-card-header--standard">
            <Link><span style={{ visibility: "hidden" }}>.</span></Link>
          </div>
        )}
        {/* swiper-categories-matrix-empty-animation fills whatever's left
            in the (already-square) card via flex — never sets its own
            aspect-ratio, so it can't fight the outer square when a header
            is also present (standard mode) eating into its share. */}
        <div className="product-card swiper-categories-matrix-empty-animation">
          <PageEmptySpace
            data={dataEmptyPage()}
            classEmpty={`AppPageEmptySpace${animationVariant}`}
          />
        </div>
        {/* <div className={inView ? "matrix-card-footer" : "empty-footer"}>
          <Link><span style={{ visibility: "hidden" }}>.</span></Link>
        </div> */}
      </div>
    </div>
  )};

  const renderFullCard = (items) => {
    const useSquareCard = renderFullCardSquare && !prodCategory;
    const cardClassName = [
      "matrix-category-card",
      // Dedicated hook (SwiperCategoriesMatrix-only) guaranteeing this
      // fills the square SwiperSlide exactly, on top of — not instead of
      // — the existing --square/--standard/--prod-category design classes.
      "swiper-categories-matrix-full-card",
      prodCategory
        ? "matrix-category-card--prod-category"
        : useSquareCard
          ? "matrix-category-card--square"
          : "matrix-category-card--standard",
      isAnimated ? animationClass : "",
    ].filter(Boolean).join(" ");
    const headerClassName = [
      "matrix-card-header",
      prodCategory ? "" : "matrix-card-header--standard",
    ].filter(Boolean).join(" ");
    const bodyClassName = [
      "matrix-card-body",
      prodCategory
        ? "matrix-card-body--prod-category"
        : useSquareCard
          ? "matrix-card-body--square"
          : "matrix-card-body--standard",
    ].filter(Boolean).join(" ");
    const footerClassName = [
      "matrix-card-footer",
      prodCategory ? "" : "matrix-card-footer--standard",
    ].filter(Boolean).join(" ");
    const cardContent = (
      <div className={cardClassName}>
        {inView && !prodCategory && !useSquareCard && (
          <div className={headerClassName}>
            <CateryName items={items} />
          </div>
        )}

        <div className={bodyClassName}>
          <PubProducts products={items} infoSource={infoSource} />
          {prodCategory && (
            <Link
              to={getProductCategoryLink(items)}
              className="matrix-see-more-link matrix-prod-category-overlay-link"
              onClick={(e) => e.stopPropagation()}
            >
              <h5 className="catery-name-text">{getProductCategoryName(items)}</h5>
            </Link>
          )}

          {useSquareCard && (
            <>
              {/* <Link
                to={getShopCategoryLink(items)}
                className="matrix-square-viewlink"
                title={getViewPageTitle()}
                onClick={(e) => e.stopPropagation()}
              >
                <GrView />
              </Link> */}
              <button
                type="button"
                className="matrix-square-overlay"
                onClick={(e) => handleCategoryClick(e, items)}
              >
                <span className="matrix-square-name">{getProductCategoryName(items)}</span>
                <span className="matrix-square-icon"><MdAdsClick /></span>
              </button>
            </>
          )}
        </div>

        {!prodCategory && !textFoorter && !useSquareCard && (
          <div className={footerClassName}>
            {inView ? (<>
              <button
                type="button"
                className="matrix-see-more-btn"
                onClick={(e) => handleCategoryClick(e, items)}
              >
                {infoSource.language(infoSource, "Link", "seeMore")}
              </button>
              </>
            ) : (
              <>
              <Link
                to={getShopCategoryLink(items)}
                className="matrix-see-more-link"
              >
                {infoSource.language(infoSource, "Link", "seeMore")}
              </Link>
              </>
            )}
          </div>
        )}
      </div>
    );

    if (prodCategory || useSquareCard) return cardContent;

    return (
      <Link
        to={textFoorter ? getShopCategoryLink(items) : "#"}
        className="matrix-category-card-link matrix-category-card-link--standard"
      >
        {cardContent}
      </Link>
    );
  };

  return (
    <div
      className={containerClass}
       style={isAnimated ? animationStyle : containerStyle}
       aria-busy={waiting || undefined}
    >
      {/* Header */}
      {inView && title && (
        <div className={headerClass} style={headerStyle}>
          <h2 className={`${titleClass} ${titlePresentation.className}`} style={titleStyle}>
            {title}
          </h2>
        </div>
      )}

      {/* Swiper */}
      <Swiper
        key={swp.key}
        {...swp.swiperProps}
        slidesPerGroupSkip={1}
        style={{
          "--swiper-navigation-color": colors?.primary || "#007399",
          "--swiper-pagination-color": colors?.primary || "#007399",
          ...swp.style,
        }}
        className={`${swiperClass} ${swp.className}`}
      >
        {newcategoriesMatrix?.map((items, i) => (
          <SwiperSlide
            key={`cat-${i}`}
            className={`swiper-categories-matrix-slide ${
              items.autantique === "vide"
                ? "swiper-categories-matrix-slide--empty"
                : ""
            }`}
          >
            <div className="swiper-categories-matrix-slide-inner">
              {items.autantique === "full" && renderFullCard(items)}
              {items.autantique === "vide" && cardEmpty(i)}
              {items.autantique === "loading" && <div className="matrix-loading-card" aria-hidden="true" />}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default SwiperCategoriesMatrix;
